import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ComponentType, CSSProperties } from 'react';

import ShopNavBar from '@eshop/pages/ShopNavBar.tsx';
import { useOptionalCatalogueAppearance, DEFAULT_CATALOGUE_DESIGN, type CatalogueDesignKey } from '@eshop/designs';
import { catalogueGridColumns } from '@theme/tokens/catalogue.ts';
import type { CatalogueTemplate } from '@eshop/templates/types';
import { useCatalogueTemplate } from '@eshop/templates/TemplateContext';
import {
  createSlotRenderer,
  getBlockRegistryForDesign,
  resolveLayoutProps,
  useCartActionsBlocks,
  type CardBlockProps,
  type DeclarativeComponent,
  type HeaderBlockProps,
  type PanelBlockProps,
} from '../blocks';
import {
  type CardTemplate,
  type CardTemplateProps,
  type CardHighlightState,
  type HeaderTemplate,
  type HeaderTemplateProps,
  type HeaderTemplateSlots,
  type PanelTemplate,
  type PanelTemplateProps,
  type TemplateCartState,
  type TemplatePaginationProps,
  type TemplateStatusBadge,
  type TemplateThemeMode,
  type WebColorToken,
} from './templateTypes';
import {
  useCatalogueDataContext,
  useCatalogueInteractionContext,
  type CatalogueDataContextValue,
  type CatalogueInteractionContextValue,
} from '@eshop/pages/catalogue/context';
import { useAppDispatch, useAppSelector } from '@store/store.tsx';
import useSiteLabels from '@eshop/hooks/useSiteLabels.ts';
import { parseAspectValue } from '../visual/shared';
import { CatalogueOfferRibbon } from '../visual/offerPresentation';
import {
  selectBlockSalesWithoutStockSetting,
  selectErpEnabled,
  selectCmsEnabled,
  selectSiteSettings,
  type SiteSettings,
} from '@store/slices/settingsSlice.tsx';
import { useThemeMode } from '@theme/hooks/useThemeMode.tsx';
import type { Item } from '@eshop/types/catalogue.types.ts';
import type { RootState } from '@store/store.tsx';
import { getImageUrl } from '@tools/utils/imageUtils.tsx';
import { message, Spin, Card } from 'antd';
import { useNavigate } from 'react-router-dom';
import { parsePrice } from '@tools/utils/common.tsx';
import { conns } from '@cfg/axiosConfig.tsx';
import {
  removeFromCartAsync,
  sendNewLineAsync,
  UpdateQtyCartLineAsync,
} from '@store/slices/cartSlice.tsx';
import { buildItemDetailUrl, childToProd } from '@eshop/pages/catalogue/CatalogueUtils.tsx';
import { useDraftOverrides } from '../context/DraftOverridesContext';
import { resolveCanBuy } from '@eshop/utils/stockPolicy.ts';
import type { TemplateSlotEntry, TemplateSlotList } from '../schema';
import {
  trackCartItemAdded,
  trackItemImpression,
} from '@eshop/utils/itemAnalytics.ts';
import { resolvePublicHome } from '@eshop/utils/publicHome.ts';

type CardSlotGroupName = 'media' | 'info' | 'meta' | 'footer';
type HeaderSlotGroupName = 'brand' | 'navigation' | 'trailing';

const cardSlotAreaByKey: Record<string, CardSlotGroupName> = {
  media: 'media',
  image: 'media',
  picture: 'media',
  title: 'info',
  heading: 'info',
  info: 'info',
  body: 'info',
  description: 'info',
  price: 'info',
  highlights: 'meta',
  badges: 'meta',
  meta: 'meta',
  footer: 'footer',
  actions: 'footer',
  cta: 'footer',
  'cart-actions': 'footer',
};

const cardSlotAreaByBlock: Record<string, CardSlotGroupName> = {
  Media: 'media',
  Title: 'info',
  Price: 'info',
  Highlights: 'meta',
  CartActions: 'footer',
};

const headerSlotAreaByKey: Record<string, HeaderSlotGroupName> = {
  brand: 'brand',
  navigation: 'navigation',
  nav: 'navigation',
  menu: 'navigation',
  trailing: 'trailing',
  actions: 'trailing',
};

const headerSlotAreaByBlock: Record<string, HeaderSlotGroupName> = {
  Brand: 'brand',
  Navigation: 'navigation',
};

const unknownCardAreas = new Set<string>();
const unknownHeaderAreas = new Set<string>();

const normalizeCardSlotArea = (value: string | undefined): CardSlotGroupName | null => {
  if (!value) {
    return null;
  }

  const normalized = value.trim().toLowerCase();
  if (!normalized) {
    return null;
  }

  if (cardSlotAreaByKey[normalized]) {
    return cardSlotAreaByKey[normalized];
  }

  switch (normalized) {
    case 'content':
    case 'summary':
    case 'details':
    case 'primary':
    case 'default':
      return 'info';
    case 'badges':
    case 'labels':
    case 'status':
      return 'meta';
    case 'actions':
    case 'controls':
    case 'buttons':
    case 'secondary':
      return 'footer';
    case 'media':
    case 'image':
    case 'visual':
      return 'media';
    default:
      if (process.env.NODE_ENV !== 'production' && !unknownCardAreas.has(normalized)) {
        unknownCardAreas.add(normalized);
        // eslint-disable-next-line no-console
        console.warn(
          `[catalogue] Área de slot no reconocida "${normalized}". Se usará el contenedor de información por defecto.`,
        );
      }
      return null;
  }
};

const normalizeHeaderSlotArea = (value: string | undefined): HeaderSlotGroupName | null => {
  if (!value) {
    return null;
  }

  const normalized = value.trim().toLowerCase();
  if (!normalized) {
    return null;
  }

  if (headerSlotAreaByKey[normalized]) {
    return headerSlotAreaByKey[normalized];
  }

  switch (normalized) {
    case 'brand':
    case 'logo':
      return 'brand';
    case 'navigation':
    case 'menu':
    case 'links':
      return 'navigation';
    case 'actions':
    case 'buttons':
    case 'trailing':
    case 'utilities':
    case 'secondary':
      return 'trailing';
    default:
      if (process.env.NODE_ENV !== 'production' && !unknownHeaderAreas.has(normalized)) {
        unknownHeaderAreas.add(normalized);
        // eslint-disable-next-line no-console
        console.warn(
          `[catalogue] Área de header no reconocida "${normalized}". Se usará la sección de acciones.`,
        );
      }
      return null;
  }
};

const resolveCardSlotArea = (entry: TemplateSlotEntry): CardSlotGroupName => {
  const area = normalizeCardSlotArea(entry.area);
  if (area) {
    return area;
  }

  if (entry.key) {
    const keyArea = cardSlotAreaByKey[entry.key.toLowerCase()];
    if (keyArea) {
      return keyArea;
    }
  }

  const blockArea = cardSlotAreaByBlock[entry.block];
  if (blockArea) {
    return blockArea;
  }

  const blockId = entry.block.toLowerCase();
  if (blockId.includes('media') || blockId.includes('image')) {
    return 'media';
  }
  if (blockId.includes('highlight') || blockId.includes('badge') || blockId.includes('tag')) {
    return 'meta';
  }
  if (blockId.includes('action') || blockId.includes('cart') || blockId.includes('cta')) {
    return 'footer';
  }

  return 'info';
};

const resolveHeaderSlotArea = (entry: TemplateSlotEntry): HeaderSlotGroupName => {
  const area = normalizeHeaderSlotArea(entry.area);
  if (area) {
    return area;
  }

  if (entry.key) {
    const keyArea = headerSlotAreaByKey[entry.key.toLowerCase()];
    if (keyArea) {
      return keyArea;
    }
  }

  const blockArea = headerSlotAreaByBlock[entry.block];
  if (blockArea) {
    return blockArea;
  }

  const blockId = entry.block.toLowerCase();
  if (blockId.includes('nav')) {
    return 'navigation';
  }
  if (blockId.includes('brand') || blockId.includes('logo')) {
    return 'brand';
  }

  return 'trailing';
};

interface CatalogueDataSelectorArgs<OwnProps> {
  data: CatalogueDataContextValue;
  template: CatalogueTemplate;
  design: CatalogueDesignKey;
  theme: TemplateThemeMode;
  site: SiteSettings | null;
  itemAspectRatio: string;
  ownProps: OwnProps;
}

interface CatalogueInteractionSelectorArgs<OwnProps> {
  interactions: CatalogueInteractionContextValue;
  ownProps: OwnProps;
}

export type CataloguePanelDataProps = Omit<PanelTemplateProps, 'cardComponent' | 'pagination'>;
export interface CataloguePanelInteractionProps {
  pagination?: TemplatePaginationProps;
}

export type CatalogueCardDataProps = Omit<CardTemplateProps, 'item' | 'index'>;

export interface BaseCataloguePanelOwnProps {
  component: PanelTemplate;
  cardComponent: CardTemplate;
  /**
   * Permite ajustar cualquier dato expuesto por el contexto antes de pasarlo al template.
   * Útil para plantillas que deseen extender el contenido sin duplicar lógica.
   */
  dataOverrides?: Partial<CataloguePanelDataProps>;
  /**
   * Personalización de la paginación (texto del botón, iconografía, etc.).
   */
  paginationOverrides?: Partial<TemplatePaginationProps>;
}

export interface BaseCatalogueCardOwnProps {
  component: CardTemplate;
  item: Item;
  index: number;
  disableDetailNavigation?: boolean;
  interactionMode?: 'live' | 'preview';
  /**
   * Ajustes visuales específicos por template sin mutar el contexto compartido.
   */
  overrides?: Partial<CatalogueCardDataProps>;
}

export interface BaseCatalogueHeaderProps {
  component: HeaderTemplate;
  /**
   * Permite sobreescribir slots o datos puntuales del header sin alterar el contrato base.
   */
  overrides?: Partial<HeaderTemplateProps>;
}

export function withCatalogueData<OwnProps extends object, InjectedProps extends object>(
  selector: (args: CatalogueDataSelectorArgs<OwnProps>) => InjectedProps,
) {
  return (Component: ComponentType<OwnProps & InjectedProps>): React.FC<OwnProps> => {
    const WithData: React.FC<OwnProps> = (props) => {
      const data = useCatalogueDataContext();
      const template = useCatalogueTemplate();
      const appearance = useOptionalCatalogueAppearance();
      const site = useAppSelector(selectSiteSettings);
      const { resolvedTheme } = useThemeMode();
      const design = appearance?.design ?? DEFAULT_CATALOGUE_DESIGN;
      const theme: TemplateThemeMode = resolvedTheme;
      // Draft overrides take priority during live editing
      const draftOverrides = useDraftOverrides();
      // Priority: draft overrides > template overrides > global eshop config > fallback
      const itemAspectRatio = useAppSelector((state: RootState) => {
        // 1. Check draft overrides first (live editing)
        if (draftOverrides?.itemAspectRatio) {
          return draftOverrides.itemAspectRatio;
        }
        // 2. Check for aspect in saved template overrides (card className or media ratio)
        const overrides = site?.catalogue_template_overrides;
        const cardClassName = overrides?.layout?.card?.className;
        if (cardClassName) {
          // Extract aspect class like 'aspect_3-4' from className string
          const aspectMatch = cardClassName.match(/aspect_\d+-\d+/);
          if (aspectMatch) {
            return aspectMatch[0];
          }
        }
        // Check for media ratio in overrides
        const mediaRatio = overrides?.layout?.media?.ratio;
        if (mediaRatio) {
          // Convert ratio "3:4" to class "aspect_3-4"
          return `aspect_${mediaRatio.replace(':', '-')}`;
        }
        // 3. Fallback to eshop config
        const aspect = state.eshop?.cfg?.item_aspect_card;
        return aspect ?? 'aspect_1-1';
      });

      const injected = selector({
        data,
        template,
        design,
        theme,
        site,
        itemAspectRatio,
        ownProps: props,
      });

      return <Component {...props} {...injected} />;
    };

    WithData.displayName = `withCatalogueData(${Component.displayName || Component.name || 'Component'})`;

    return WithData;
  };
}

export function withCatalogueInteractions<OwnProps extends object, InjectedProps extends object>(
  selector: (args: CatalogueInteractionSelectorArgs<OwnProps>) => InjectedProps,
) {
  return (Component: ComponentType<OwnProps & InjectedProps>): React.FC<OwnProps> => {
    const WithInteractions: React.FC<OwnProps> = (props) => {
      const interactions = useCatalogueInteractionContext();

      const injected = selector({
        interactions,
        ownProps: props,
      });

      return <Component {...props} {...injected} />;
    };

    WithInteractions.displayName = `withCatalogueInteractions(${Component.displayName || Component.name || 'Component'})`;

    return WithInteractions;
  };
}

const normalizeStatusBadges = (tags: CatalogueDataContextValue['filters']['statusTags']):
  | TemplateStatusBadge[]
  | undefined => {
  const visible = tags.filter((tag) => tag.isVisible);
  if (!visible.length) {
    return undefined;
  }

  return visible.map((tag) => ({
    key: tag.key,
    label: tag.label,
  }));
};

const panelDataSelector = (
  args: CatalogueDataSelectorArgs<BaseCataloguePanelOwnProps & CataloguePanelInteractionProps>,
): CataloguePanelDataProps => {
  const { data, design, theme } = args;

  const hero = {
    eyebrow: data.filters.heroEyebrow,
    title: data.filters.categoryTitle,
    description: data.filters.heroDescription,
    resultCount: data.totalProducts,
  };

  const category = data.selectedCategory
    ? {
      id: data.selectedCategory.id,
      name: data.selectedCategory.name,
    }
    : undefined;

  return {
    design,
    theme,
    hero,
    category,
    layout: {
      mode: data.viewMode,
      grid: data.gridConfig.breakpoints,
      gutter: data.gridConfig.gutter,
      itemCardSize: data.gridConfig.itemCardSize,
    },
    items: data.items,
    banners: data.banners,
    status: {
      isLoading: data.isItemLoading,
      hasResults: data.hasResults,
      badges: normalizeStatusBadges(data.filters.statusTags),
    },
  };
};

const panelInteractionsSelector = (
  args: CatalogueInteractionSelectorArgs<BaseCataloguePanelOwnProps>,
): CataloguePanelInteractionProps => {
  const { interactions } = args;

  if (!interactions.hasMore) {
    return { pagination: undefined };
  }

  return {
    pagination: {
      hasMore: interactions.hasMore,
      onLoadMore: interactions.loadMore,
      isLoading: interactions.isLoading,
    },
  };
};

const cardDataSelector = (
  args: CatalogueDataSelectorArgs<BaseCatalogueCardOwnProps>,
): CatalogueCardDataProps => {
  const { template, design, theme, itemAspectRatio, data } = args;

  const accentColor =
    (template.visualTemplate?.meta?.accentColor ?? '--web-color-3') as WebColorToken;
  const designMatrix = catalogueGridColumns(design, data.gridConfig.itemCardSize);
  const matrixMax = Math.max(...Object.values(designMatrix));
  const configuredColumns = Object.values(data.gridConfig.columns ?? {})
    .filter((value): value is number => typeof value === 'number' && value > 0);
  const derivedMax = configuredColumns.length ? Math.max(...configuredColumns) : undefined;
  const computedMax = derivedMax ?? matrixMax;
  const priorityImageCount = Math.max(1, Math.min(computedMax, matrixMax));

  return {
    design,
    theme,
    viewMode: template.itemCardStyle,
    mediaAspectRatio: itemAspectRatio,
    priorityImageCount,
    accentColor,
  };
};

export type BaseCataloguePanelProps =
  BaseCataloguePanelOwnProps & CataloguePanelInteractionProps & CataloguePanelDataProps;

type BasePanelProps = BaseCataloguePanelProps;

const BasePanelInner: React.FC<BasePanelProps> = ({
  component: PanelComponent,
  cardComponent,
  dataOverrides,
  paginationOverrides,
  pagination,
  ...panelDataRest
}) => {
  const template = useCatalogueTemplate();
  const appearance = useOptionalCatalogueAppearance();
  const design = appearance?.design ?? template.visualTemplate?.key ?? DEFAULT_CATALOGUE_DESIGN;
  const registry = getBlockRegistryForDesign(design);
  const panelData = panelDataRest as CataloguePanelDataProps;

  const mergedData: CataloguePanelDataProps = {
    ...panelData,
    ...(dataOverrides ?? {}),
  };

  let mergedPagination: TemplatePaginationProps | undefined = pagination;

  if (paginationOverrides) {
    if (pagination) {
      mergedPagination = {
        ...pagination,
        ...paginationOverrides,
        hasMore:
          typeof paginationOverrides.hasMore === 'boolean'
            ? paginationOverrides.hasMore
            : pagination.hasMore,
      };
    } else if (typeof paginationOverrides.hasMore === 'boolean') {
      mergedPagination = paginationOverrides as TemplatePaginationProps;
    }
  }

  const panelProps: PanelBlockProps = {
    ...mergedData,
    cardComponent,
    pagination: mergedPagination,
  };

  const layoutItemCardSize = panelProps.layout?.itemCardSize;
  const sizeClassName = layoutItemCardSize ? `catalogue-panel--size-${layoutItemCardSize}` : null;

  const declarativeConfig =
    (PanelComponent as DeclarativeComponent<PanelBlockProps>).renderBlocks ?? null;
  const panelSlots = declarativeConfig?.slots;
  const panelLayout = declarativeConfig?.layout ?? null;
  const heroSlots = declarativeConfig?.regions?.hero ?? null;
  const heroLayout = declarativeConfig?.layoutByRegion?.hero ?? null;

  const renderPanelBlocks = useMemo(
    () =>
      panelSlots
        ? createSlotRenderer<PanelBlockProps>({
          region: 'panel',
          slots: panelSlots,
          registry: registry.panel,
        })
        : null,
    [panelSlots, registry.panel],
  );

  const panelLayoutProps = useMemo(() => resolveLayoutProps(panelLayout), [panelLayout]);
  const sectionStyle = panelLayoutProps.style;

  const renderHeroBlocks = useMemo(
    () =>
      heroSlots
        ? createSlotRenderer<PanelBlockProps>({
          region: 'panel',
          slots: heroSlots,
          registry: registry.panel,
        })
        : null,
    [heroSlots, registry.panel],
  );

  const heroLayoutProps = useMemo(() => {
    const resolved = resolveLayoutProps(heroLayout);
    if (heroSlots && typeof resolved.fullWidth === 'undefined') {
      return { ...resolved, fullWidth: true };
    }

    return resolved;
  }, [heroLayout, heroSlots]);
  const heroAspectClass = useMemo(() => {
    if (!heroLayoutProps.className) {
      return null;
    }
    const match = heroLayoutProps.className.match(/\baspect_\d+-\d+\b/i);
    return match ? match[0] : null;
  }, [heroLayoutProps.className]);
  const heroAspectRatioFromClass = useMemo(
    () => parseAspectValue(heroAspectClass ?? undefined),
    [heroAspectClass],
  );
  const heroWrapperStyle = useMemo(() => {
    if (!heroLayoutProps.style && !heroLayoutProps.fullWidth) {
      return heroLayoutProps.style;
    }

    const baseStyle = heroLayoutProps.style ? { ...heroLayoutProps.style } : undefined;

    if (baseStyle && 'aspectRatio' in baseStyle && baseStyle.aspectRatio !== undefined) {
      delete baseStyle.aspectRatio;
    }

    if (!heroLayoutProps.fullWidth) {
      return baseStyle;
    }

    return {
      ...(baseStyle ?? {}),
      // width: '100%',
      // marginLeft: 'calc(50% - 50vw)',
      // marginRight: 'calc(50% - 50vw)',
    } as CSSProperties;
  }, [heroLayoutProps.fullWidth, heroLayoutProps.style]);

  const heroContainerStyle = useMemo(() => {
    const layoutAspect = heroLayoutProps.style?.aspectRatio;
    const resolvedAspect = heroAspectRatioFromClass ?? layoutAspect;
    if (!resolvedAspect) {
      return undefined;
    }

    const style: CSSProperties = {
      aspectRatio: resolvedAspect,
    };
    (style as Record<string, string>)['--catalogue-hero-aspect'] = String(resolvedAspect);
    return style;
  }, [heroAspectRatioFromClass, heroLayoutProps.style?.aspectRatio]);

  const heroInnerClassName = useMemo(
    () =>
      ['catalogue-panel__hero-region-inner', heroAspectClass]
        .filter(Boolean)
        .join(' '),
    [heroAspectClass],
  );

  if (renderPanelBlocks) {
    const baseClassName = `catalogue-panel theme-${panelProps.theme} catalogue-panel--${panelProps.design}`;
    const className = [baseClassName, panelLayoutProps.className, sizeClassName]
      .filter(Boolean)
      .join(' ');
    const panelContent = renderPanelBlocks(panelProps);

    // logError('------> BasePanelInner - heroInnerClassName: ', heroInnerClassName, 'heroContainerStyle: ', heroContainerStyle);

    const heroContent = renderHeroBlocks?.(panelProps) ?? null;
    const heroNode = heroContent ? (
      <div
        className={[
          'catalogue-panel__hero-region',
          heroLayoutProps.className,
          heroLayoutProps.fullWidth ? 'catalogue-panel__hero-region--full-width' : null,
        ]
          .filter(Boolean)
          .join(' ')}
        style={heroWrapperStyle}
        data-region="hero"
        data-template={template.visualTemplate?.key}
      >
        <div className={heroInnerClassName} style={heroContainerStyle}>
          {heroContent}
        </div>
      </div>
    ) : null;

    return (
      <>
        {heroNode}
        <section
          className={className}
          style={sectionStyle}
          data-design={panelProps.design}
          data-template={template.visualTemplate?.key}
          data-view-mode={panelProps.layout.mode}
        >
          <Spin
            spinning={Boolean(panelProps.status?.isLoading && !panelProps.status?.hasResults)}
            delay={250}
          >
            <div className="catalogue-panel__content">{panelContent}</div>
          </Spin>
        </section>
      </>
    );
  }

  return <PanelComponent {...panelProps} />;
};

export type BaseCatalogueCardProps = BaseCatalogueCardOwnProps & CatalogueCardDataProps;

type BaseCardProps = BaseCatalogueCardProps;

export const BaseCardInner: React.FC<BaseCardProps> = ({
  component: CardComponent,
  item,
  index,
  disableDetailNavigation = false,
  interactionMode = 'live',
  overrides,
  ...cardDataRest
}) => {
  const template = useCatalogueTemplate();
  const registry = getBlockRegistryForDesign(template.visualTemplate?.key);
  const cardData = cardDataRest as CatalogueCardDataProps;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const cartItems = useAppSelector((state: RootState) => state.cart.cartItems);
  const cartIsLoading = useAppSelector((state: RootState) => state.cart.isLoading);
  const erpEnabled = useAppSelector(selectErpEnabled);
  const blockSalesWithoutStock = useAppSelector(selectBlockSalesWithoutStockSetting);
  const { catalog } = useSiteLabels();
  const catalogLabels = catalog.labels;
  const catalogFlags = catalog.flags;
  const hasVariants = Array.isArray(item.children) && item.children.length > 0;
  const [selectedChild, setSelectedChild] = useState<Item | null>(null);
  const impressionRef = useRef<HTMLDivElement | null>(null);
  const impressionTrackedRef = useRef(false);

  useEffect(() => {
    if (interactionMode === 'preview') return;

    const node = impressionRef.current;
    if (!node || impressionTrackedRef.current || typeof IntersectionObserver === 'undefined') {
      return;
    }

    let visibleTimer: number | null = null;
    const renderedAt = new Date().toISOString();
    const listInstanceId = `catalog:${window.location.pathname}`;

    const clearVisibleTimer = () => {
      if (visibleTimer) {
        window.clearTimeout(visibleTimer);
        visibleTimer = null;
      }
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry || document.visibilityState !== 'visible') {
          clearVisibleTimer();
          return;
        }

        if (entry.intersectionRatio < 0.5) {
          clearVisibleTimer();
          return;
        }

        if (visibleTimer || impressionTrackedRef.current) {
          return;
        }

        visibleTimer = window.setTimeout(() => {
          if (impressionTrackedRef.current || document.visibilityState !== 'visible') {
            return;
          }
          impressionTrackedRef.current = true;
          trackItemImpression(item, {
            surface: 'catalog',
            listInstanceId,
            position: index + 1,
            visibleRatio: Math.round(entry.intersectionRatio * 100) / 100,
            visibleTimeMs: 1000,
            renderedAt,
          });
          observer.disconnect();
        }, 1000);
      },
      { threshold: [0.5, 0.75, 1] },
    );

    observer.observe(node);
    return () => {
      clearVisibleTimer();
      observer.disconnect();
    };
  }, [index, interactionMode, item]);

  useEffect(() => {
    if (!hasVariants) {
      setSelectedChild(null);
      return;
    }

    if (!selectedChild) {
      return;
    }

    const nextChild = item.children?.find((child) => String(child.id) === String(selectedChild.id));
    if (!nextChild) {
      setSelectedChild(null);
      return;
    }

    // Only update if the child changed meaningfully (avoid infinite loops when
    // childToProd creates new object references on every render for preview items)
    if (nextChild.is_preview && String(nextChild.id) !== String(selectedChild.id)) {
      setSelectedChild(childToProd(nextChild));
    }
  }, [hasVariants, item.children, selectedChild?.id]);

  const linesForItem = useMemo(
    () =>
      cartItems.filter(
        (line) => line.main_item_id === item.id || line.item_id === item.id,
      ),
    [cartItems, item.id],
  );

  const totalQuantity = useMemo(
    () => linesForItem.reduce((sum, line) => sum + (line.quantity ?? 0), 0),
    [linesForItem],
  );

  useEffect(() => {
    if (!hasVariants || selectedChild) {
      return;
    }

    const firstChildInCart = linesForItem.find((line) =>
      item.children?.some((child) => String(child.id) === String(line.item_id)),
    );

    if (firstChildInCart?.item) {
      setSelectedChild(firstChildInCart.item);
    }
  }, [hasVariants, item.children, linesForItem, selectedChild]);

  const isInCart = linesForItem.length > 0;
  const selectedLine = useMemo(() => {
    if (!selectedChild) {
      return undefined;
    }

    return linesForItem.find((line) => String(line.item_id) === String(selectedChild.id));
  }, [linesForItem, selectedChild]);

  const isActiveInCart = hasVariants ? Boolean(selectedLine) : isInCart;
  const primaryLine = hasVariants
    ? selectedLine
    : (isInCart ? linesForItem[0] : undefined);
  const currentItem = selectedChild ?? item;

  const stockPolicy = useMemo(
    () =>
      resolveCanBuy({
        erpEnabled,
        blockSalesWithoutStock,
        item: {
          stock: currentItem.stock,
          stock_state: currentItem.stock_state,
          can_buy: currentItem.can_buy,
          disponibilidad: currentItem.disponibilidad,
        },
      }),
    [
      blockSalesWithoutStock,
      currentItem.can_buy,
      currentItem.disponibilidad,
      currentItem.stock,
      currentItem.stock_state,
      erpEnabled,
    ],
  );

  const shouldBlockStock = stockPolicy.policyEnforced;
  const allowBackorderPurchases = stockPolicy.allowBackorderPurchases;

  const maxStock = useMemo(() => {
    if (!shouldBlockStock || typeof currentItem.stock !== 'number') {
      return undefined;
    }

    const available = Math.max(0, Math.floor(currentItem.stock));

    if (!isActiveInCart) {
      return available > 0 ? available : 0;
    }

    if (available <= 0) {
      return totalQuantity || undefined;
    }

    return Math.max(available, totalQuantity || 0);
  }, [currentItem.stock, isActiveInCart, shouldBlockStock, totalQuantity]);

  const minQuantity = 1;
  const normalizedMaxQuantity =
    typeof maxStock === 'number' ? maxStock : undefined;

  const openItemDetail = useCallback(() => {
    if (disableDetailNavigation) {
      return;
    }

    const fallbackUrl = buildItemDetailUrl(item);
    const targetUrl = item.url ?? fallbackUrl;
    if (!targetUrl) {
      return;
    }

    if (/^https?:\/\//i.test(targetUrl)) {
      window.location.href = targetUrl;
    } else {
      navigate(targetUrl);
    }
  }, [disableDetailNavigation, item, navigate]);

  const handleSelectVariant = useCallback(
    async (variantId?: number) => {
      if (!variantId || !item.children?.length) {
        setSelectedChild(null);
        return;
      }

      const selected = item.children.find((child) => Number(child.id) === Number(variantId));
      if (!selected) {
        void message.info('No logramos encontrar la variante seleccionada.');
        return;
      }

      if (selected.is_preview) {
        setSelectedChild(childToProd(selected));
        return;
      }

      try {
        const childResponse = await conns<Item>('eshop:catalogue:get_data_from_id', [variantId]).get();
        setSelectedChild(childResponse.data);
      } catch (_error) {
        setSelectedChild(childToProd(selected));
      }
    },
    [item.children],
  );

  const addToCart = useCallback(() => {
    if (cartIsLoading) {
      return;
    }

    const hasOptions = Array.isArray(currentItem.options) && currentItem.options.length > 0;

    if (hasVariants && !selectedChild) {
      void message.info('Seleccioná una variante para continuar.');
      return;
    }

    if (stockPolicy.isBlockedByStock) {
      if (catalogLabels.blockedMessage) {
        void message.info(catalogLabels.blockedMessage);
      }
      return;
    }

    const priceInfo = parsePrice(currentItem.price);
    if (!priceInfo || priceInfo.value <= 0) {
      void message.info('Este producto no está disponible temporalmente.');
      return;
    }

    if (hasOptions) {
      void message.info('Este producto requiere opciones adicionales desde la vista detalle.');
      openItemDetail();
      return;
    }

    trackCartItemAdded(currentItem, 1, { surface: 'catalog', source: 'template_card' });
    dispatch(sendNewLineAsync({ item: currentItem, quantity: 1 }));
  }, [
    cartIsLoading,
    catalogLabels.blockedMessage,
    currentItem,
    dispatch,
    hasVariants,
    openItemDetail,
    selectedChild,
    stockPolicy,
  ]);

  const removeFromCart = useCallback(() => {
    if (!isActiveInCart || !primaryLine || cartIsLoading) {
      return;
    }

    void dispatch(removeFromCartAsync({ itemId: primaryLine.item_id, lineId: primaryLine.id }));
  }, [cartIsLoading, dispatch, isActiveInCart, primaryLine]);

  const updateQuantity = useCallback(
    (requestedQuantity: number) => {
      if (!isActiveInCart || !primaryLine) {
        if (cartIsLoading) {
          return;
        }

        const desired = Number.isFinite(requestedQuantity)
          ? Math.max(minQuantity, Math.floor(requestedQuantity))
          : minQuantity;

        if (desired < minQuantity) {
          return;
        }

        if (hasVariants && !selectedChild) {
          void message.info('Seleccioná una variante para continuar.');
          return;
        }

        trackCartItemAdded(currentItem, desired, { surface: 'catalog', source: 'template_quantity' });
        void dispatch(sendNewLineAsync({ item: currentItem, quantity: desired }));
        return;
      }

      if (cartIsLoading) {
        return;
      }

      const raw = Number.isFinite(requestedQuantity)
        ? Math.floor(requestedQuantity)
        : minQuantity;

      if (raw < minQuantity) {
        removeFromCart();
        return;
      }

      const capped =
        typeof maxStock === 'number' ? Math.min(raw, maxStock) : raw;

      if (typeof maxStock === 'number' && raw > maxStock) {
        void message.info('Cantidad máxima disponible en stock.');
      }

      const currentQuantity = hasVariants ? (selectedLine?.quantity ?? 0) : totalQuantity;
      if (capped === currentQuantity) {
        return;
      }

      void dispatch(
        UpdateQtyCartLineAsync({ prodId: primaryLine.item_id, quantity: capped }),
      );
    },
    [
      cartIsLoading,
      dispatch,
      currentItem,
      hasVariants,
      isActiveInCart,
      maxStock,
      primaryLine,
      removeFromCart,
      selectedLine,
      selectedChild,
      totalQuantity,
    ],
  );

  const increaseQuantity = useCallback(() => {
    if (cartIsLoading) {
      return;
    }

    if (!isActiveInCart) {
      addToCart();
      return;
    }

    const baseQuantity = hasVariants ? (selectedLine?.quantity ?? 0) : totalQuantity;
    updateQuantity(baseQuantity + 1);
  }, [addToCart, cartIsLoading, hasVariants, isActiveInCart, selectedLine, totalQuantity, updateQuantity]);

  const decreaseQuantity = useCallback(() => {
    if (!isActiveInCart || !primaryLine) {
      return;
    }

    const baseQuantity = hasVariants ? (selectedLine?.quantity ?? 0) : totalQuantity;
    updateQuantity(baseQuantity - 1);
  }, [hasVariants, isActiveInCart, primaryLine, selectedLine, totalQuantity, updateQuantity]);

  const handleManualQuantityChange = useCallback(
    (nextQuantity: number) => {
      updateQuantity(nextQuantity);
    },
    [updateQuantity],
  );

  const handleViewDetailFromTemplate = useCallback(
    (_item: Item) => {
      if (interactionMode === 'preview' || disableDetailNavigation) {
        return;
      }

      openItemDetail();
    },
    [disableDetailNavigation, interactionMode, openItemDetail],
  );

  const handleAddToCartFromTemplate = useCallback(
    (_item: Item) => {
      if (interactionMode === 'preview') return;
      addToCart();
    },
    [addToCart, interactionMode],
  );

  const handleIncreaseQuantityFromTemplate = useCallback(
    (_item: Item) => {
      if (interactionMode === 'preview') return;
      increaseQuantity();
    },
    [increaseQuantity, interactionMode],
  );

  const handleDecreaseQuantityFromTemplate = useCallback(
    (_item: Item) => {
      if (interactionMode === 'preview') return;
      decreaseQuantity();
    },
    [decreaseQuantity, interactionMode],
  );

  const handleRemoveItemFromTemplate = useCallback(
    (_item: Item) => {
      if (interactionMode === 'preview') return;
      removeFromCart();
    },
    [interactionMode, removeFromCart],
  );

  const handleChangeQuantityFromTemplate = useCallback(
    (_item: Item, nextQuantity: number) => {
      if (interactionMode === 'preview') return;
      handleManualQuantityChange(nextQuantity);
    },
    [handleManualQuantityChange, interactionMode],
  );

  const interactions = useMemo(
    () => ({
      onAddToCart: handleAddToCartFromTemplate,
      onViewDetail: handleViewDetailFromTemplate,
      onIncreaseQuantity: handleIncreaseQuantityFromTemplate,
      onDecreaseQuantity: handleDecreaseQuantityFromTemplate,
      onRemoveItem: handleRemoveItemFromTemplate,
      onChangeQuantity: handleChangeQuantityFromTemplate,
    }),
    [
      handleAddToCartFromTemplate,
      handleChangeQuantityFromTemplate,
      handleDecreaseQuantityFromTemplate,
      handleIncreaseQuantityFromTemplate,
      handleRemoveItemFromTemplate,
      handleViewDetailFromTemplate,
    ],
  );

  const cartState = useMemo(
    () => {
      const effectiveQuantity = hasVariants
        ? selectedLine?.quantity
        : totalQuantity;
      const isCurrentInCart = isActiveInCart;

      if (!isCurrentInCart) {
        return {
          quantity: undefined,
          isInCart: isCurrentInCart,
          isUpdating: cartIsLoading,
          minQuantity,
          maxQuantity: normalizedMaxQuantity,
          canIncrease:
            normalizedMaxQuantity === undefined
              ? true
              : normalizedMaxQuantity > 0,
          canDecrease: false,
          canDelete: false,
        } satisfies TemplateCartState;
      }

      const resolvedQuantity = typeof effectiveQuantity === 'number' ? effectiveQuantity : minQuantity;
      const isAtMinimum = resolvedQuantity <= minQuantity;

      return {
        quantity: resolvedQuantity,
        isInCart: isCurrentInCart,
        isUpdating: cartIsLoading,
        minQuantity,
        maxQuantity: normalizedMaxQuantity,
        canIncrease:
          normalizedMaxQuantity === undefined
            ? true
            : resolvedQuantity < normalizedMaxQuantity,
        canDecrease: resolvedQuantity > minQuantity,
        canDelete: isAtMinimum,
      } satisfies TemplateCartState;
    },
    [
      cartIsLoading,
      hasVariants,
      isActiveInCart,
      minQuantity,
      normalizedMaxQuantity,
      selectedLine,
      totalQuantity,
    ],
  );

  const highlightState = useMemo(() => {
    const isOutOfStockState = currentItem.stock_state === 'out_of_stock';
    const soldOutLabel = catalogLabels.outOfStockBadge;

    if (shouldBlockStock) {
      if ((typeof currentItem.stock === 'number' && currentItem.stock <= 0) || isOutOfStockState) {
        if (soldOutLabel) {
          return { isSoldOut: true, label: soldOutLabel } satisfies CardHighlightState;
        }
        return { isSoldOut: true } satisfies CardHighlightState;
      }
    } else if (isOutOfStockState) {
      if (soldOutLabel) {
        return { label: soldOutLabel } satisfies CardHighlightState;
      }
      return undefined;
    }

    if (allowBackorderPurchases && currentItem.stock_state === 'backorder') {
      if (catalogFlags.hideBackorderLabel) {
        return undefined;
      }

      const label = catalogLabels.backorderBadge;
      if (!label) {
        return undefined;
      }
      return { label } satisfies CardHighlightState;
    }

    return undefined;
  }, [
    allowBackorderPurchases,
    catalogFlags.hideBackorderLabel,
    catalogLabels.backorderBadge,
    catalogLabels.outOfStockBadge,
    currentItem.stock,
    currentItem.stock_state,
    shouldBlockStock,
  ]);

  const variantState = useMemo(() => {
    if (!hasVariants || !item.children?.length) {
      return undefined;
    }

    const quantityByChild = linesForItem.reduce<Record<string, number>>((acc, line) => {
      const key = String(line.item_id);
      acc[key] = (acc[key] ?? 0) + (line.quantity ?? 0);
      return acc;
    }, {});

    return {
      hasVariants: true,
      selectedVariantId: selectedChild ? Number(selectedChild.id) : undefined,
      selectedVariant: selectedChild,
      totalInCart: totalQuantity,
      isLoading: cartIsLoading,
      onSelect: (variantId?: number) => {
        void handleSelectVariant(variantId);
      },
      options: item.children.map((child) => ({
        id: Number(child.id),
        label: child.name,
        inCartQuantity: quantityByChild[String(child.id)] ?? 0,
        disabled: child.can_buy === false || child.stock_state === 'out_of_stock',
      })),
    };
  }, [
    cartIsLoading,
    handleSelectVariant,
    hasVariants,
    item.children,
    linesForItem,
    selectedChild,
    totalQuantity,
  ]);

  const mergedData: CatalogueCardDataProps = {
    ...cardData,
    ...(overrides ?? {}),
  };

  const cardProps: CardBlockProps = {
    ...mergedData,
    item,
    index,
    interactions,
    cartState,
    variantState,
    highlightState,
  };

  const declarativeConfig =
    (CardComponent as DeclarativeComponent<CardBlockProps>).renderBlocks ?? null;
  const cardSlots = declarativeConfig?.slots;
  const cardLayout = declarativeConfig?.layout ?? null;
  const cardRegions = declarativeConfig?.regions ?? null;
  const cardRegionLayouts = declarativeConfig?.layoutByRegion ?? null;

  const hasExplicitCardRegions = Boolean(
    (cardRegions?.media?.length ?? 0) ||
    (cardRegions?.content?.length ?? 0) ||
    (cardRegions?.meta?.length ?? 0) ||
    (cardRegions?.footer?.length ?? 0),
  );

  const cardSlotGroups = useMemo(() => {
    if (hasExplicitCardRegions) {
      return {
        media: cardRegions?.media?.length ? cardRegions.media : null,
        info: cardRegions?.content?.length ? cardRegions.content : null,
        meta: cardRegions?.meta?.length ? cardRegions.meta : null,
        footer: cardRegions?.footer?.length ? cardRegions.footer : null,
      } as {
        media: TemplateSlotList | null;
        info: TemplateSlotList | null;
        meta: TemplateSlotList | null;
        footer: TemplateSlotList | null;
      };
    }

    if (!cardSlots?.length) {
      return {
        media: null as TemplateSlotList | null,
        info: null as TemplateSlotList | null,
        meta: null as TemplateSlotList | null,
        footer: null as TemplateSlotList | null,
      };
    }

    const groups: Record<CardSlotGroupName, TemplateSlotList> = {
      media: [],
      info: [],
      meta: [],
      footer: [],
    };

    cardSlots.forEach((entry) => {
      const area = resolveCardSlotArea(entry);
      groups[area].push(entry);
    });

    return {
      media: groups.media.length ? groups.media : null,
      info: groups.info.length ? groups.info : null,
      meta: groups.meta.length ? groups.meta : null,
      footer: groups.footer.length ? groups.footer : null,
    };
  }, [cardRegions, cardSlots, hasExplicitCardRegions]);

  const renderMediaBlocks = useMemo(
    () =>
      cardSlotGroups.media
        ? createSlotRenderer<CardBlockProps>({
          region: 'card',
          slots: cardSlotGroups.media,
          registry: registry.card,
        })
        : null,
    [cardSlotGroups.media, registry.card],
  );

  const renderInfoBlocks = useMemo(
    () =>
      cardSlotGroups.info
        ? createSlotRenderer<CardBlockProps>({
          region: 'card',
          slots: cardSlotGroups.info,
          registry: registry.card,
        })
        : null,
    [cardSlotGroups.info, registry.card],
  );

  const renderMetaBlocks = useMemo(
    () =>
      cardSlotGroups.meta
        ? createSlotRenderer<CardBlockProps>({
          region: 'card',
          slots: cardSlotGroups.meta,
          registry: registry.card,
        })
        : null,
    [cardSlotGroups.meta, registry.card],
  );

  const renderFooterBlocks = useMemo(
    () =>
      cardSlotGroups.footer
        ? createSlotRenderer<CardBlockProps>({
          region: 'card',
          slots: cardSlotGroups.footer,
          registry: registry.card,
        })
        : null,
    [cardSlotGroups.footer, registry.card],
  );

  const cardLayoutProps = useMemo(() => resolveLayoutProps(cardLayout), [cardLayout]);
  const mediaRegionLayout = cardRegionLayouts?.media ?? null;
  const contentRegionLayout = cardRegionLayouts?.content ?? null;
  const metaRegionLayout = cardRegionLayouts?.meta ?? null;

  const mediaLayoutProps = useMemo(() => resolveLayoutProps(mediaRegionLayout), [mediaRegionLayout]);
  const contentLayoutProps = useMemo(() => resolveLayoutProps(contentRegionLayout), [contentRegionLayout]);
  const metaLayoutProps = useMemo(() => resolveLayoutProps(metaRegionLayout), [metaRegionLayout]);

  const wrapWithBadge = (content: React.ReactNode) => {
    return <CatalogueOfferRibbon item={item}>{content}</CatalogueOfferRibbon>;
  };

  if (renderMediaBlocks || renderInfoBlocks || renderMetaBlocks || renderFooterBlocks) {
    const baseClassName = `catalogue-card catalogue-card--${cardProps.design}`;
    const className = [baseClassName, cardLayoutProps.className].filter(Boolean).join(' ');
    const accentStyle: CSSProperties | undefined = cardProps.accentColor
      ? ({ ['--catalogue-card-accent' as string]: `var(${cardProps.accentColor})` } as CSSProperties)
      : undefined;
    const mergedStyle = { ...(cardLayoutProps.style ?? {}), ...(accentStyle ?? {}) } as CSSProperties;
    const style = Object.keys(mergedStyle).length > 0 ? mergedStyle : undefined;

    const mediaContent = renderMediaBlocks?.(cardProps);
    const infoContent = renderInfoBlocks?.(cardProps);
    const metaContent = renderMetaBlocks?.(cardProps);

    const shouldWrapMedia = hasExplicitCardRegions || Boolean(mediaLayoutProps.className || mediaLayoutProps.style);

    const mediaNode = shouldWrapMedia && mediaContent
      ? (
        <div
          className={[
            'catalogue-card__media-region',
            mediaLayoutProps.className,
            mediaLayoutProps.fullWidth ? 'catalogue-card__media-region--full-width' : null,
          ]
            .filter(Boolean)
            .join(' ')}
          style={mediaLayoutProps.style}
          data-region="media"
        >
          {mediaContent}
        </div>
      )
      : mediaContent;

    // Llamamos al hook de forma incondicional en el nivel superior del componente
    const cartActionsNodes = useCartActionsBlocks(cardProps);

    // Generamos las acciones del footer, aplanando los componentes que devuelven múltiples nodos
    const footerActions = useMemo(() => {
      if (!cardSlotGroups.footer || cardSlotGroups.footer.length === 0) return undefined;
      const actionsArr = cardSlotGroups.footer.flatMap((slot): React.ReactNode[] => {
        if (slot.block === 'CartActions') {
          return cartActionsNodes;
        }
        const Component = registry.card[slot.block];
        if (!Component) return [];
        return [<Component key={slot.key} {...slot.props} {...cardProps} />];
      });
      return actionsArr.length > 0 ? actionsArr : undefined;
    }, [cardSlotGroups.footer, cardProps, registry.card, cartActionsNodes]);

    return wrapWithBadge(
      <div ref={impressionRef} style={{ width: '100%', height: '100%' }}>
        <Card
          hoverable
          className={className}
          style={style}
          data-index={index}
          data-design={cardProps.design}
          data-template={template.visualTemplate?.key}
          data-view-mode={cardProps.viewMode}
          cover={mediaNode}
          actions={footerActions}
        >
          {(infoContent || metaContent) && (
            <Card.Meta
              description={
                <div
                  className={[
                    'catalogue-card__body',
                    contentLayoutProps.className,
                    contentLayoutProps.fullWidth ? 'catalogue-card__body--full-width' : null,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  style={contentLayoutProps.style}
                  data-region="content"
                >
                  {infoContent && <div className="catalogue-card__info">{infoContent}</div>}
                  {metaContent && (
                    <div
                      className={['catalogue-card__meta', metaLayoutProps.className]
                        .filter(Boolean)
                        .join(' ')}
                      style={metaLayoutProps.style}
                      data-region="content.meta"
                    >
                      {metaContent}
                    </div>
                  )}
                </div>
              }
            />
          )}
        </Card>
      </div>
    );
  }

  return wrapWithBadge(
    <div ref={impressionRef} style={{ width: '100%', height: '100%' }}>
      <CardComponent
        {...mergedData}
        item={item}
        index={index}
        interactions={interactions}
        cartState={cartState}
        variantState={variantState}
        highlightState={highlightState}
      />
    </div>
  );
};

const deriveBrandName = (site: SiteSettings | null): string => {
  const rawSuffix = site?.name?.trim();
  if (rawSuffix) {
    const cleaned = rawSuffix.replace(/^[|•→\-–—\s]+/u, '').trim();
    if (cleaned) {
      return cleaned;
    }
  }

  const storeName = site?.store_address?.first_name?.trim();
  if (storeName) {
    return storeName;
  }

  return 'E-Shop';
};

const createHeaderBrand = (
  site: SiteSettings | null,
  homeUrl: string,
): HeaderTemplateProps['brand'] => {
  const brandName = deriveBrandName(site);
  const brandDescription = site?.description ?? undefined;
  const logoUrl = site?.logo ? getImageUrl(site.logo).srcDefault : undefined;

  return {
    name: brandName,
    description: brandDescription ?? undefined,
    homeUrl,
    logoUrl,
    logoAlt: brandName,
  };
};

const createHeaderSlots = (
  template: CatalogueTemplate,
  overrides?: HeaderTemplateSlots,
): HeaderTemplateSlots | undefined => {
  const baseSlots: HeaderTemplateSlots = {
    navigation: (
      <ShopNavBar
        cartShape={template.cartButtonShape}
        cartIconColor={template.cartIconColor}
      />
    ),
  };

  if (!overrides) {
    return baseSlots;
  }

  return {
    ...baseSlots,
    ...overrides,
  };
};

const PanelWithData = withCatalogueData<BaseCataloguePanelOwnProps & CataloguePanelInteractionProps, CataloguePanelDataProps>(
  panelDataSelector,
)(BasePanelInner);

export const BaseCataloguePanel = withCatalogueInteractions<BaseCataloguePanelOwnProps, CataloguePanelInteractionProps>(
  panelInteractionsSelector,
)(PanelWithData);

export const BaseCatalogueCard = withCatalogueData<BaseCatalogueCardOwnProps, CatalogueCardDataProps>(
  cardDataSelector,
)(BaseCardInner);

export const BaseCatalogueHeader: React.FC<BaseCatalogueHeaderProps> = ({
  component: HeaderComponent,
  overrides,
}) => {
  const template = useCatalogueTemplate();
  const appearance = useOptionalCatalogueAppearance();
  const site = useAppSelector(selectSiteSettings);
  const cmsEnabled = useAppSelector(selectCmsEnabled);
  const { resolvedTheme } = useThemeMode();
  const publicHomeUrl = useAppSelector((state: RootState) => {
    const urls = state.eshop?.cfg?.urls;
    return resolvePublicHome(cmsEnabled, urls);
  });

  const design = appearance?.design ?? DEFAULT_CATALOGUE_DESIGN;
  const registry = getBlockRegistryForDesign(design);
  const theme: TemplateThemeMode = resolvedTheme;

  const { brand: brandOverride, slots: slotsOverride, ...restOverrides } = overrides ?? {};

  const brand = {
    ...createHeaderBrand(site, publicHomeUrl),
    ...(brandOverride ?? {}),
  };

  const slots = createHeaderSlots(template, slotsOverride);

  const accentColor = template.visualTemplate?.meta?.accentColor;

  const headerProps: HeaderTemplateProps = {
    design,
    theme,
    brand,
    cartButtonShape: restOverrides.cartButtonShape ?? template.cartButtonShape,
    cartIconColor: restOverrides.cartIconColor ?? accentColor,
    navigationLinks: restOverrides.navigationLinks,
    activeNavigationKey: restOverrides.activeNavigationKey,
    slots,
  };

  const declarativeConfig =
    (HeaderComponent as DeclarativeComponent<HeaderBlockProps>).renderBlocks ?? null;
  const headerSlotsConfig = declarativeConfig?.slots;
  const headerLayout = declarativeConfig?.layout ?? null;

  const headerSlotGroups = useMemo(() => {
    if (!headerSlotsConfig?.length) {
      return {
        brand: null as TemplateSlotList | null,
        navigation: null as TemplateSlotList | null,
        trailing: null as TemplateSlotList | null,
      };
    }

    const groups: Record<HeaderSlotGroupName, TemplateSlotList> = {
      brand: [],
      navigation: [],
      trailing: [],
    };

    headerSlotsConfig.forEach((entry) => {
      const area = resolveHeaderSlotArea(entry);
      groups[area].push(entry);
    });

    return {
      brand: groups.brand.length ? groups.brand : null,
      navigation: groups.navigation.length ? groups.navigation : null,
      trailing: groups.trailing.length ? groups.trailing : null,
    };
  }, [headerSlotsConfig]);

  const renderBrandBlocks = useMemo(
    () =>
      headerSlotGroups.brand
        ? createSlotRenderer<HeaderBlockProps>({
          region: 'header',
          slots: headerSlotGroups.brand,
          registry: registry.header,
        })
        : null,
    [headerSlotGroups.brand, registry.header],
  );

  const renderNavigationBlocks = useMemo(
    () =>
      headerSlotGroups.navigation
        ? createSlotRenderer<HeaderBlockProps>({
          region: 'header',
          slots: headerSlotGroups.navigation,
          registry: registry.header,
        })
        : null,
    [headerSlotGroups.navigation, registry.header],
  );

  const renderTrailingBlocks = useMemo(
    () =>
      headerSlotGroups.trailing
        ? createSlotRenderer<HeaderBlockProps>({
          region: 'header',
          slots: headerSlotGroups.trailing,
          registry: registry.header,
        })
        : null,
    [headerSlotGroups.trailing, registry.header],
  );

  const headerLayoutProps = useMemo(() => resolveLayoutProps(headerLayout), [headerLayout]);

  if (renderBrandBlocks || renderNavigationBlocks || renderTrailingBlocks) {
    const baseClassName = `eshop-header theme-${theme} catalogue-header catalogue-header--${design}`;
    const className = [baseClassName, headerLayoutProps.className].filter(Boolean).join(' ');
    const headerBlockProps: HeaderBlockProps = headerProps;
    const brandContent = renderBrandBlocks?.(headerBlockProps);
    const navigationContent = renderNavigationBlocks?.(headerBlockProps);
    const trailingContent = renderTrailingBlocks?.(headerBlockProps);
    const hasTrailingSection = Boolean(navigationContent || trailingContent);

    return (
      <header
        className={className}
        style={headerLayoutProps.style}
        data-design={design}
        data-template={template.visualTemplate?.key}
      >
        <div className="eshop-header__inner">
          {brandContent}
          {hasTrailingSection && (
            <div className="eshop-header__actions">
              {navigationContent}
              {trailingContent}
            </div>
          )}
        </div>
      </header>
    );
  }

  return <HeaderComponent {...headerProps} />;
};

/**
 * Propiedades estandarizadas que cada template visual debe respetar.
 * Permiten documentar el contrato base y la procedencia de los datos.
 */
export const standardProps = {
  header: {
    design: 'Catálogo → CatalogueAppearanceContext.design',
    theme: 'UI → useThemeMode().resolvedTheme',
    brand: {
      name: 'SiteSettings.name',
      description: 'SiteSettings.description',
      homeUrl: 'state.eshop.cfg.urls.catalogue | urls.home',
      logoUrl: 'SiteSettings.logo → getImageUrl(...)',
    },
    cartButtonShape: 'CatalogueTemplate.cartButtonShape',
    cartIconColor: 'VisualTemplate.meta.accentColor (p. ej. var(--web-color-3))',
    slots: 'Navigation/Config se inyectan vía overrides usando componentes existentes',
  },
  card: {
    design: 'CatalogueAppearanceContext.design',
    theme: 'UI theme',
    viewMode: 'CatalogueTemplate.itemCardStyle',
    mediaAspectRatio: 'state.eshop.cfg.item_aspect_card',
    accentColor: 'VisualTemplate.meta.accentColor ⇒ var(--web-color-X)',
    interactions: 'Callbacks opcionales provistos por futuras extensiones',
  },
  panel: {
    hero: 'useCatalogueFilters → eyebrow/title/description/resultCount',
    category: 'useCatalogueData.selectedCategory',
    layout: 'Responsive spans/gutter calculados en useCatalogueData.gridConfig',
    items: 'state.eshop.data.items filtrados',
    banners: 'BannersApi vía react-query',
    status: 'Loading & badges derivados de filters.statusTags',
    pagination: 'loadMore/isLoading desde CatalogueInteractionContext',
  },
  tokens: {
    accent: 'var(--web-color-3)',
    surface: 'var(--web-color-1)',
    spacingUnit: 'var(--catalogue-spacing-unit)',
    gridGap: 'var(--catalogue-grid-gap)',
  },
} as const;

export type BaseCataloguePanelComponent = ComponentType<BaseCataloguePanelProps>;
export type BaseCatalogueCardComponent = ComponentType<BaseCatalogueCardProps>;
export type BaseCatalogueHeaderComponent = ComponentType<BaseCatalogueHeaderProps>;
